<template>
  <v-container fluid class="pa-4">
    <div class="d-flex align-center justify-space-between mb-1 flex-wrap" style="gap: 12px">
      <div>
        <span class="text-h5 font-weight-bold">
          <v-icon left color="primary">{{ isWfhMode ? 'mdi-home-account' : 'mdi-calendar-check' }}</v-icon>
          {{ isWfhMode ? 'Approve / Decline Work From Home' : 'Approve / Decline Leave' }}
        </span>
        <p class="text-body-2 grey--text mb-0 mt-1">
          {{ isWfhMode
            ? 'Review and act on pending work-from-home requests from your team'
            : 'Review and act on pending leave requests from your team' }}
        </p>
      </div>
      <v-chip outlined>
        <span class="font-weight-bold">{{ pendingLeaves.length }} pending</span>
      </v-chip>
    </div>

    <template v-if="isLoadingPending">
      <v-row class="mt-2">
        <v-col v-for="n in 3" :key="n" cols="12" md="6" lg="4">
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
    </template>
    <div v-else-if="pendingLeaves.length === 0" class="text-center py-12">
      <v-icon size="64" color="grey lighten-1">
        {{ isWfhMode ? 'mdi-home-account' : 'mdi-calendar-check-outline' }}
      </v-icon>
      <p class="text-h6 grey--text mt-4">
        {{ isWfhMode ? 'No pending work-from-home requests' : 'No pending leave requests' }}
      </p>
    </div>
    <v-row v-else class="mt-2">
      <v-col v-for="item in pendingLeaves" :key="item.id" cols="12" md="6" lg="4">
        <v-card outlined class="pa-3 leave-request-card">
          <div class="d-flex align-center mb-3">
            <v-avatar size="36" color="primary" class="mr-3">
              <span class="white--text text-caption font-weight-bold">{{ getInitials(item.user?.name) }}</span>
            </v-avatar>
            <div>
              <div class="font-weight-bold">{{ item.user?.name || "Unknown user" }}</div>
              <div class="text-caption grey--text">{{ item.leave_type?.name || "No type" }}</div>
            </div>
            <v-spacer />
            <v-chip x-small color="orange" text-color="white">Pending</v-chip>
          </div>

          <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
            <v-chip small label>
              <v-icon x-small left>mdi-calendar</v-icon>
              {{ formatLeaveDateRange(item) }}
            </v-chip>
            <v-chip small label>
              <v-icon x-small left>mdi-clock-outline</v-icon>
              {{ formatLeaveDuration(item) }}
            </v-chip>
          </div>

          <div v-if="item.note" class="text-caption grey--text font-italic mb-3 leave-note">
            {{ item.note }}
          </div>

          <v-textarea v-model="decisionNotes[item.id]" outlined dense rows="2" hide-details
            placeholder="Add a note for the employee (optional)" class="mb-3"></v-textarea>

          <v-divider class="mb-3" />

          <div class="d-flex justify-end flex-wrap" style="gap: 8px">
            <v-btn small text color="primary" class="text-none mr-auto" @click="openConflicts(item)">
              <v-icon left small>mdi-calendar-search</v-icon>
              Check business unit calendar
            </v-btn>
            <v-btn small outlined color="error" class="text-none" :loading="updatingId === item.id"
              @click="declineRequest(item)">
              Decline
            </v-btn>
            <v-btn small color="success" dark class="text-none" :loading="updatingId === item.id"
              @click="approveRequest(item)">
              Approve
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <div class="d-flex align-center mt-6 mb-1">
      <span class="text-h6 font-weight-bold">
        <v-icon left color="primary">mdi-history</v-icon>
        History
      </span>
    </div>
    <template v-if="isLoadingHistory">
      <v-card outlined>
        <v-skeleton-loader v-for="n in 3" :key="n" type="list-item-two-line" class="pa-3" />
      </v-card>
    </template>
    <div v-else-if="historyLeaves.length === 0" class="text-center py-8">
      <p class="text-body-2 grey--text mb-0">No decisions made yet</p>
    </div>
    <v-card v-else outlined>
      <div v-for="item in historyLeaves" :key="item.id" class="request-row">
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
          <div>
            <span class="font-weight-bold text-body-2">{{ item.user?.name }}</span>
            <span class="text-caption grey--text ml-2">{{ item.leave_type?.name }}</span>
          </div>
          <v-chip x-small label :color="getLeaveStatusColor(item.status?.id)" text-color="white">
            <v-icon x-small left>{{ getLeaveStatusIcon(item.status?.id) }}</v-icon>
            {{ item.status?.name }}
          </v-chip>
        </div>
        <div class="text-caption grey--text mt-1">
          {{ formatLeaveDateRange(item) }} &middot; {{ formatLeaveDuration(item) }}
        </div>
        <div v-if="item.reason" class="text-caption grey--text font-italic mt-1">
          Your note: {{ item.reason }}
        </div>
      </div>
    </v-card>

    <LeaveConflictDialog
      v-model="conflictOpen"
      :request="conflictItem"
      :is-wfh="isWfhMode"
      :updating="!!conflictItem && updatingId === conflictItem.id"
      :initial-note="(conflictItem && decisionNotes[conflictItem.id]) || ''"
      @approve="decideFromDialog('approve', $event)"
      @decline="decideFromDialog('decline', $event)"
    />
  </v-container>
</template>

<script>
/**vuex */
import { mapGetters } from "vuex";
/**shared helpers */
import {
  formatLeaveDateRange,
  formatLeaveDuration,
  getLeaveStatusColor,
  getLeaveStatusIcon,
  getInitials,
} from "./leaveOvertimeUtils";

import LeaveConflictDialog from "../leave-management/LeaveConflictDialog.vue";

const WFH_TYPE_NAME = "Work From Home";

export default {
  components: { LeaveConflictDialog },
  props: {
    // "leave" (default) = normal leave approvals, WFH filtered out.
    // "wfh"  = only Work From Home requests.
    // "all"  = no split (used where there is a single combined approvals list).
    // Same list, same API either way — just scoped client-side.
    mode: { type: String, default: "leave" },
  },
  inject: ["refreshApprovalCounts"],
  data() {
    return {
      pendingLeavesRaw: [],
      leaveStatuses: [],
      overview: [],
      isLoadingPending: true,
      isLoadingHistory: true,
      updatingId: null,
      decisionNotes: {},
      conflictItem: null,
      conflictOpen: false,
    };
  },
  async mounted() {
    await Promise.all([this.getToApproveLeaveRequest(), this.getLeaveStatus(), this.getLeaveOverview()]);
  },
  computed: {
    ...mapGetters({
      user: "auth/get_user",
    }),
    isWfhMode() {
      return this.mode === "wfh";
    },
    matchesMode() {
      return (item) => this.mode === "all" || this.isWfhItem(item) === this.isWfhMode;
    },
    pendingLeaves() {
      return this.pendingLeavesRaw.filter(this.matchesMode);
    },
    approvedStatusId() {
      const found = this.leaveStatuses.find((s) => (s.name || "").toLowerCase().includes("approv"));
      return found ? found.id : 2;
    },
    declinedStatusId() {
      const found = this.leaveStatuses.find((s) => (s.name || "").toLowerCase().includes("declin"));
      return found ? found.id : 3;
    },
    historyLeaves() {
      return this.overview
        .filter(
          (item) =>
            item.approver_id === this.user.id &&
            [2, 3].includes(item.leave_status_id) &&
            this.matchesMode(item)
        )
        .sort((a, b) => b.id - a.id);
    },
  },
  methods: {
    formatLeaveDateRange,
    formatLeaveDuration,
    getLeaveStatusColor,
    getLeaveStatusIcon,
    getInitials,
    isWfhItem(item) {
      return ((item.leave_type && item.leave_type.name) || "").trim().toLowerCase() === WFH_TYPE_NAME.toLowerCase();
    },
    openConflicts(item) {
      this.conflictItem = item;
      this.conflictOpen = true;
    },
    async decideFromDialog(action, note) {
      const item = this.conflictItem;
      if (!item) return;
      this.$set(this.decisionNotes, item.id, note);
      if (action === "approve") await this.approveRequest(item);
      else await this.declineRequest(item);
      // updateStatus swallows errors; only close once the request left the pending list
      if (!this.pendingLeavesRaw.some((r) => r.id === item.id)) this.conflictOpen = false;
    },
    async approveRequest(item) {
      await this.updateStatus(item, this.approvedStatusId);
    },
    async declineRequest(item) {
      await this.updateStatus(item, this.declinedStatusId);
    },
    async updateStatus(item, statusId) {
      this.updatingId = item.id;
      try {
        const tp = {
          leave_status_id: statusId,
          is_updated: 1,
          user_id: item.user?.id,
          count: item.count,
          reason: this.decisionNotes[item.id] || null,
        };
        await this.updateRequest(item.id, tp);
        const statusName = this.leaveStatuses.find((s) => s.id === statusId)?.name || "Unknown";
        const label = this.isWfhMode ? "WFH request" : "Leave request";
        this.$toast.success(`${label} ${statusName.toLowerCase()} successfully`);
        this.$delete(this.decisionNotes, item.id);
        await Promise.all([this.getToApproveLeaveRequest(), this.getLeaveOverview()]);
        if (typeof this.refreshApprovalCounts === "function") {
          this.refreshApprovalCounts();
        }
      } catch (error) {
        console.log("error on updating request status - component level", error);
        this.$toast.error("Failed to update leave request status");
      } finally {
        this.updatingId = null;
      }
    },

    /**
     * get leave status
     */
    async getLeaveStatus() {
      try {
        const { data } = await this.$axios.get("/api/leave/status");
        this.leaveStatuses = data;
      } catch (error) {
        console.log("error on fetching leave status - service layer", error);
      }
    },

    /**
     * get to approve leave request
     */
    async getToApproveLeaveRequest() {
      this.isLoadingPending = true;
      try {
        const { data } = await this.$axios.get("/api/leave/approval");
        this.pendingLeavesRaw = data;
        data.forEach((item) => {
          if (!(item.id in this.decisionNotes)) {
            this.$set(this.decisionNotes, item.id, "");
          }
        });
      } catch (error) {
        console.log("error on fetching to approve leave request - service layer", error);
      } finally {
        this.isLoadingPending = false;
      }
    },

    /**
     * get all leave requests (used to derive this approver's decision history)
     */
    async getLeaveOverview() {
      this.isLoadingHistory = true;
      try {
        const { data } = await this.$axios.get("/api/leave/overview");
        this.overview = data;
      } catch (error) {
        console.log("error on fetching leave overview - service layer", error);
      } finally {
        this.isLoadingHistory = false;
      }
    },

    /**
     * update leave request
     * @params
     * record_id?: int
     * item?: object
     */
    async updateRequest(record_id, item) {
      return await this.$axios.patch(`/api/leave/approval/${record_id}`, item);
    },
  },
};
</script>

<style scoped>
.leave-request-card {
  height: 100%;
  transition: box-shadow 0.2s ease;
}

.leave-request-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.leave-note {
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.request-row {
  padding: 10px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.request-row:last-child {
  border-bottom: none;
}
</style>
